import React, { useEffect, useState } from 'react'
import { Upload, Button, Select, Typography, Space, Form, message } from 'antd'
import { UploadOutlined, DeleteOutlined, FileExcelOutlined } from '@ant-design/icons'
import { uploadVpsSource, deleteVpsSource } from '../../actions/admin'
const { Title, Text } = Typography
const { Option } = Select

// Editor for the optional VPS account whitelist. The admin uploads an Excel
// file (parsed and stored server-side), then binds one of its columns to a form
// field. The account numbers themselves never reach the browser — only the
// column headers and counts are returned in `vpsSource`.
const VpsWhitelistEditor = ({ docId, fields = [], vpsSource = null, vpsWhitelist = null, onSourceChange, onChange }) => {
  const [busy, setBusy] = useState(false)

  // The two selections are held locally so a half-finished binding (only column
  // or only field chosen) isn't lost. We bubble a complete { fieldKey, column }
  // up to the parent — or null while incomplete — without reading those values
  // back from the parent on every change (which would clear the other half).
  const [fieldKey, setFieldKey] = useState((vpsWhitelist && vpsWhitelist.fieldKey) || undefined)
  const [column, setColumn] = useState((vpsWhitelist && vpsWhitelist.column) || undefined)

  // Resync only when the source file itself changes (new upload or removal),
  // not on every selection.
  const sourceKey = vpsSource ? vpsSource.fileName : null
  useEffect(() => {
    setFieldKey((vpsWhitelist && vpsWhitelist.fieldKey) || undefined)
    setColumn((vpsWhitelist && vpsWhitelist.column) || undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey])

  const emit = (nextField, nextColumn) => {
    onChange(nextField && nextColumn ? { fieldKey: nextField, column: nextColumn } : null)
  }

  const handleUpload = async (file) => {
    setBusy(true)
    try {
      const doc = await uploadVpsSource(docId, file)
      onSourceChange(doc)
      message.success('VPS whitelist uploaded')
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message
      message.error(msg || 'Could not upload whitelist')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async () => {
    setBusy(true)
    try {
      const doc = await deleteVpsSource(docId)
      onSourceChange(doc)
      message.success('VPS whitelist removed')
    } catch (err) {
      message.error('Could not remove whitelist')
    } finally {
      setBusy(false)
    }
  }

  const columns = (vpsSource && vpsSource.columns) || []
  const bound = !!(fieldKey && column)

  return (
    <div>
      <Title level={5}>VPS account whitelist</Title>
      <Text type="secondary">
        Optional. Upload an Excel file of valid VPS account numbers, then choose which column holds them and which
        form field to check against. Customers whose VPS account is not on the list cannot proceed. Separators
        (spaces, dots) are ignored when matching.
      </Text>

      <div style={{ marginTop: 12 }}>
        {vpsSource ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <FileExcelOutlined />
              <Text strong>{vpsSource.fileName}</Text>
              <Button size="small" danger icon={<DeleteOutlined />} loading={busy} onClick={handleRemove}>
                Remove
              </Button>
            </Space>
            <Form layout="vertical" style={{ marginTop: 8 }}>
              <Form.Item
                label="VPS column in the Excel file"
                validateStatus={!column ? 'warning' : ''}
                help={!column ? 'Select the column holding the VPS numbers' : undefined}
              >
                <Select
                  placeholder="Select column"
                  value={column}
                  onChange={(v) => { setColumn(v); emit(fieldKey, v) }}
                  style={{ maxWidth: 360 }}
                  allowClear
                >
                  {columns.map(c => (
                    <Option key={c.header} value={c.header}>{c.header} ({c.count})</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Validate this form field"
                validateStatus={!fieldKey ? 'warning' : ''}
                help={!fieldKey ? 'Select which form field to validate' : undefined}
              >
                <Select
                  placeholder="Select field"
                  value={fieldKey}
                  onChange={(v) => { setFieldKey(v); emit(v, column) }}
                  style={{ maxWidth: 360 }}
                  allowClear
                >
                  {fields.map(f => (
                    <Option key={f.key} value={f.key}>{f.label || f.key}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Text type={bound ? 'success' : 'secondary'}>
                {bound
                  ? 'Validation active — remember to save the configuration.'
                  : 'Validation is inactive until both a column and a field are selected.'}
              </Text>
            </Form>
          </Space>
        ) : (
          <Upload
            accept=".xlsx"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => { handleUpload(file); return false }}
          >
            <Button icon={<UploadOutlined />} loading={busy}>Upload Excel whitelist (.xlsx)</Button>
          </Upload>
        )}
      </div>
    </div>
  )
}

export default VpsWhitelistEditor
